import { CHANNEL_LABEL, fmtMoney, fmtMoneyList, get, html, pct, STAGES, stageLabel } from "../lib.js";
import { tr } from "../i18n.js";
import { ErrorBox, Loading, PageHeader, useLoad } from "../ui.js";
import { PageGuide } from "../guide.js";
import { LINK_SOURCE_LABEL } from "./links.js";
import { stepLabel } from "../setup-steps.js";

function Kpi({ label, value, hint }) {
  return html`<div class="card kpi"><div class="kpi-label">${label}</div><div class="kpi-value">${value}</div>${hint && html`<div class="muted small">${hint}</div>`}</div>`;
}

function seconds(s) {
  if (s == null) return "—";
  if (s < 90) return tr("{n}s", { n: Math.round(s) });
  return tr("{n}m", { n: Math.round(s / 60) });
}

function DailyChart({ daily }) {
  const days = Object.entries(daily);
  const max = Math.max(1, ...days.map(([, d]) => Math.max(d.conversations, d.bookings)));
  const w = 100 / days.length;
  if (!days.some(([, d]) => d.conversations || d.bookings)) return html`<p class="muted">${tr("No activity yet. Send a test message from the AI agent page.")}</p>`;
  return html`<figure class="chart">
    <svg viewBox="0 0 100 40" preserveAspectRatio="none" role="img" aria-label=${tr("Conversations and bookings per day")}>
      ${days.map(([day, d], i) => html`
        <g><title>${tr("{day}: {conversations} conversations, {bookings} bookings", { day, conversations: d.conversations, bookings: d.bookings })}</title>
          <rect x=${i * w + w * 0.1} width=${w * 0.38} y=${40 - (d.conversations / max) * 38} height=${(d.conversations / max) * 38} class="bar a" />
          <rect x=${i * w + w * 0.52} width=${w * 0.38} y=${40 - (d.bookings / max) * 38} height=${(d.bookings / max) * 38} class="bar b" />
        </g>`)}
    </svg>
    <figcaption class="legend"><span class="sw a"></span>${tr("Conversations")} <span class="sw b"></span>${tr("Bookings")} <span class="muted">· ${tr("{from} → {to}", { from: days[0]?.[0], to: days[days.length - 1]?.[0] })}</span></figcaption>
  </figure>`;
}

function Setup({ t }) {
  // Which steps, their order and when each is done come from the server (src/onboarding/checklist.ts),
  // the same answer Claude gets from the MCP server's get_setup_status; only the wording is ours.
  const [checklist] = useLoad(() => get(`${t.base}/setup/checklist`), [t.id]);
  if (!checklist || checklist.complete) return null;
  return html`<section class="card setup">
    <h2>${tr("Get set up")} <span class="muted small">${checklist.done}/${checklist.total}</span></h2>
    <ol>${checklist.steps.map((s) => html`<li class=${s.done ? "done" : ""}><a href=${s.href}>${stepLabel(s, tr)}</a></li>`)}</ol>
  </section>`;
}

const SOURCE_LABEL = { direct: "Direct / unknown", meta_ads: "Meta ads", meta_post: "Meta post", "m.me": "m.me link", google: "Google", facebook: "Facebook", instagram: "Instagram", chat_link: "Chat Link" };

/** New leads in the period by where they first came from, and how many booked or became customers. */
function Sources({ rows, currency }) {
  if (!rows?.length) return null;
  return html`<section class="card table-wrap">
    <h2>${tr("Where leads come from")}</h2>
    <p class="muted small">${tr("First touch of each new contact: UTM tags and referrer on the website chat, Click-to-WhatsApp and Messenger ads, m.me links.")}</p>
    <table class="table">
      <thead><tr><th>${tr("Source")}</th><th>${tr("Campaign")}</th><th>${tr("Leads")}</th><th>${tr("Made a booking")}</th><th>${tr("Became customers")}</th><th>${tr("Booking rate")}</th><th>${tr("Revenue")}</th></tr></thead>
      <tbody>${rows.map((r) => html`<tr>
        <td><strong>${SOURCE_LABEL[r.source] ? tr(SOURCE_LABEL[r.source]) : LINK_SOURCE_LABEL[r.source] ? tr(LINK_SOURCE_LABEL[r.source]) : r.source}</strong></td><td class="small" dir="auto">${r.campaign ?? html`<span class="muted">—</span>`}</td>
        <td>${r.leads}</td><td>${r.booked}</td><td>${r.customers}</td><td>${pct(r.leads ? r.booked / r.leads : null)}</td><td>${r.revenue ? fmtMoney(r.revenue, currency) : html`<span class="muted">—</span>`}</td>
      </tr>`)}</tbody>
    </table>
  </section>`;
}

/** Missed Call Text-Back: calls nobody answered, the WhatsApp messages sent, and what came of them. */
function MissedCalls({ m }) {
  if (!m?.received) return null;
  return html`<section class="card">
    <div class="section-head"><h2>${tr("Missed calls texted back")}</h2><a class="small" href="/channels">${tr("Settings")}</a></div>
    <div class="link-stats">
      <span><strong>${m.received}</strong> ${tr("missed calls")}</span>
      <span><strong>${m.texted}</strong> ${tr("texted back")}</span>
      <span><strong>${m.replied}</strong> ${tr("replied")}</span>
      <span><strong>${m.bookings}</strong> ${tr("bookings")}</span>
      <span><strong>${fmtMoneyList(m.value)}</strong> ${tr("booked, in value")}</span>
    </div>
    <p class="muted small">${tr("Bookings these customers made after the message, valued like Booked by AI: the price on confirmed and attended visits.")}</p>
  </section>`;
}

function Today({ t }) {
  const [data] = useLoad(() => get(`${t.base}/brief`), [t.id]);
  if (!data) return null;
  const b = data.brief;
  const items = [
    { n: b.appointments.count, label: tr("appointments today"), href: "/bookings", detail: b.appointments.items.slice(0, 3).map((a) => `${a.time} ${a.service}`).join(" · ") },
    { n: b.waiting.count, label: tr("chats waiting for a person"), href: "/inbox", warn: b.waiting.count > 0, detail: b.waiting.names.join(", ") },
    { n: b.tasks.overdue + b.tasks.today, label: tr("tasks due"), href: "/tasks", warn: b.tasks.overdue > 0, detail: b.tasks.overdue ? tr("{n} overdue", { n: b.tasks.overdue }) : "" },
    { n: b.outcomes_to_mark, label: tr("past visits to mark"), href: "/bookings", warn: b.outcomes_to_mark > 0, detail: tr("Attended or no-show?") },
  ];
  return html`<section class="card today">
    <div class="section-head"><h2>${tr("Today")}</h2><span class="muted small">${tr("Yesterday: {chats} new chats, {booked} booked by AI{value}, {cancelled} cancelled, {noShows} no-shows", { chats: b.yesterday.conversations, booked: b.yesterday.ai_bookings, value: b.yesterday.ai_bookings_value ? ` (${fmtMoney(b.yesterday.ai_bookings_value.amount, b.yesterday.ai_bookings_value.currency)})` : "", cancelled: b.yesterday.cancelled, noShows: b.yesterday.no_shows })}</span></div>
    <div class="today-grid">${items.map((i) => html`<a class="today-item ${i.warn ? "warn" : ""}" href=${i.href}>
      <strong>${i.n}</strong><span>${i.label}</span>${i.detail && html`<span class="muted small">${i.detail}</span>`}
    </a>`)}</div>
  </section>`;
}

function ReviewSuggestions({ t }) {
  const [data] = useLoad(() => get(`${t.base}/qa`), [t.id]);
  if (!data?.suggestions?.length || t.role === "agent") return null;
  return html`<section class="card">
    <div class="section-head"><h2>${tr("Improve your AI agent")}</h2><a class="btn small" href="/agent#weekly-review">${tr("Review suggestions")}</a></div>
    <p>${tr("{n} improvements from real conversations are waiting for your approval.", { n: data.suggestions.length })}</p>
    <ul>${data.suggestions.slice(0, 3).map((s) => html`<li dir="auto">${s.title}</li>`)}</ul>
    <p class="muted small">${tr("Nothing changes until you approve it.")}</p>
  </section>`;
}

export function OverviewPage({ t }) {
  const [data, , error] = useLoad(() => get(`${t.base}/analytics/summary`), [t.id]);
  return html`<div>
    <${PageHeader} title=${tr("Overview")} subtitle=${tr("Today, then the last 30 days")} />
    <${PageGuide} id="overview" />
    <${Setup} t=${t} />
    <${Today} t=${t} />
    <${ReviewSuggestions} t=${t} />
    ${error && html`<${ErrorBox} error=${error} />`}
    ${!data && !error && html`<${Loading} />`}
    ${data && html`
      <div class="kpis">
        <${Kpi} label=${tr("Booked by AI, in value")} value=${fmtMoneyList(data.revenue.by_agent)} hint=${tr("{total} booked in total · {attended} attended", { total: fmtMoneyList(data.revenue.total), attended: fmtMoneyList(data.revenue.attended) })} />
        ${data.revenue.packages_sold > 0 && html`<${Kpi} label=${tr("Packages sold")} value=${fmtMoneyList(data.revenue.packages)} hint=${tr("{n} packages", { n: data.revenue.packages_sold })} />`}
        <${Kpi} label=${tr("Conversations")} value=${data.conversations_started} hint=${tr("{n} customer messages", { n: data.messages.inbound })} />
        <${Kpi} label=${tr("Booked by AI")} value=${data.bookings.by_agent} hint=${tr("{n} bookings in total", { n: data.bookings.created })} />
        <${Kpi} label=${tr("Chat → booking")} value=${pct(data.conversion_rate)} />
        <${Kpi} label=${tr("Median first reply")} value=${seconds(data.median_response_seconds)} />
        <${Kpi} label=${tr("Arrived after hours")} value=${pct(data.after_hours_share)} hint=${tr("Inquiries answered while you were closed")} />
        <${Kpi} label=${tr("No-show rate")} value=${pct(data.bookings.no_show_rate)} />
        ${data.nps.sent > 0 && html`<${Kpi} label=${tr("Recommend score (NPS)")} value=${data.nps.score ?? "—"} hint=${tr("{answered} of {sent} answered · {detractors} unhappy", { answered: data.nps.answered, sent: data.nps.sent, detractors: data.nps.detractors })} />`}
        <${Kpi} label=${tr("Handed to a person")} value=${data.handoffs} />
        <${Kpi} label=${tr("AI cost")} value=${`$${data.llm.reduce((s, u) => s + u.est_cost_usd, 0).toFixed(2)}`} hint=${tr("{n} model calls", { n: data.llm.reduce((s, u) => s + u.requests, 0) })} />
      </div>
      <div class="grid-2">
        <section class="card"><h2>${tr("Daily activity")}</h2><${DailyChart} daily=${data.daily} /></section>
        <section class="card">
          <h2>${tr("Lead funnel")}</h2>
          <div class="funnel">
            ${STAGES.map((s) => {
              const n = data.funnel[s] ?? 0;
              const max = Math.max(1, ...Object.values(data.funnel));
              return html`<a class="funnel-row" href=${`/contacts?stage=${s}`}><span>${stageLabel(s)}</span><span class="funnel-bar"><span style=${{ width: `${(n / max) * 100}%` }}></span></span><strong>${n}</strong></a>`;
            })}
          </div>
          <h3>${tr("By channel")}</h3>
          <p>${Object.entries(data.by_channel).map(([k, v]) => html`<span class="badge neutral">${CHANNEL_LABEL[k] ?? k}: ${v}</span> `)}
            ${!Object.keys(data.by_channel).length && html`<span class="muted">${tr("No conversations yet.")}</span>`}</p>
        </section>
      </div>
      <${MissedCalls} m=${data.missed_calls} />
      <${Sources} rows=${data.by_source} currency=${data.revenue.total[0]?.currency ?? "ILS"} />`}
  </div>`;
}

